import {
  fetchAndActivate,
  getAll,
  getRemoteConfig as getFirebaseRemoteConfig,
  isSupported,
} from 'firebase/remote-config'
import { toBoolean } from 'fitify-utils/src/properties'
import { createContext, useContext, useEffect, useState } from 'react'

const IS_REMOTE_CONFIG_FETCH_ENABLED = toBoolean(
  process.env.NEXT_PUBLIC_REMOTE_CONFIG_FETCH_ENABLED
)

const getRemoteConfig = async (): Promise<Record<string, any>> => {
  const isRemoteConfigSupported = await isSupported()

  // If remote config is not supported, return empty object
  if (!isRemoteConfigSupported) {
    return {}
  }

  const firebaseRemoteConfig = getFirebaseRemoteConfig()

  await fetchAndActivate(firebaseRemoteConfig)
  const configParams = getAll(firebaseRemoteConfig)

  const remoteConfigProps: RemoteConfig = {}
  for (const key of Object.keys(configParams)) {
    const value: any = configParams[key]
    remoteConfigProps[key] = value._value
  }

  return remoteConfigProps
}

export const useRemoteConfig = (
  rcDefaults: RemoteConfig
): { config: RemoteConfig; isLoaded: boolean } => {
  const [config, setConfig] = useState<RemoteConfig>(rcDefaults)
  const [isLoaded, setIsLoaded] = useState(!IS_REMOTE_CONFIG_FETCH_ENABLED)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getRemoteConfig()
        if (Object.keys(data).length) {
          setConfig(data)
        }
      } finally {
        setIsLoaded(true)
      }
    }

    if (IS_REMOTE_CONFIG_FETCH_ENABLED) {
      void fetchData()
    }
  }, [])

  return { config, isLoaded }
}

type Await<T> = T extends Promise<infer U> ? U : never

export type RemoteConfig = Await<ReturnType<typeof getRemoteConfig>>
export const RemoteContext = createContext<RemoteConfig>({})

export const useRemoteConfigContext = (): RemoteConfig => {
  const context = useContext(RemoteContext)
  if (!context) {
    throw new Error(
      'useRemoteConfigContext must be used within a RemoteContext.Provider'
    )
  }
  return context
}
